import { Box, Stack } from '@mui/material'
import { grey } from '@mui/material/colors'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import LabeledTextField from '@/components/dataDisplay/LabeledTextField'
import BidStatusChip from '@/features/bidding/components/BidStatusChip'
import type { BidUpdateCreatedBy } from '@/features/bidding/constants'
import type { BidMetaData } from '@/features/bidding/types/bid'
import { getCreatedByEmoji, getCreatedByLabel } from '@/features/bidding/utils/createdBy'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends BidVersionPtusToolbarProps {}
}

type BidVersionPtusToolbarProps = {
  bid: BidMetaData
  activationGroupSelectorComponent?: ReactElement
  bidSelectorComponent?: ReactElement
  expandAllRowsComponent?: ReactElement
  actionComponent?: ReactElement
  createdBy?: BidUpdateCreatedBy
}

const BidVersionPtusToolbar = ({
  bid,
  activationGroupSelectorComponent,
  bidSelectorComponent,
  expandAllRowsComponent,
  actionComponent,
  createdBy,
}: BidVersionPtusToolbarProps) => {
  const { t } = useTranslation()

  return (
    <>
      <Stack direction="row" marginY={2} spacing={1}>
        {activationGroupSelectorComponent ?? null}
        {bidSelectorComponent ?? null}
        {expandAllRowsComponent ?? null}
        {actionComponent && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            {actionComponent}
          </>
        )}
      </Stack>
      <Stack direction="row" marginY={2} spacing={1}>
        <TableTextSummary label={t('bidding.create_bid.select_portfolio')} text={bid.portfolio.name} />
        <TableTextSummary
          label={t('bidding.create_bid.select_market_program')}
          text={t(`bidding.market_program.${bid.marketProgram}`)}
        />

        <TableTextSummary label={t('bidding.results.bid_date')} text={bid.deliveryDay.getStartOfDay().toFormat('DD')} />
        <Box sx={{ bgcolor: grey[50], borderRadius: 1, p: 2 }} width={'100%'}>
          <CustomTypography color={'#9e9e9e'} variant="caption">
            {t('bidding.results.status')}
          </CustomTypography>
          <BidStatusChip status={bid.status} />
        </Box>
        {createdBy && (
          <Box sx={{ bgcolor: grey[50], borderRadius: 1, p: 2 }} width={'100%'}>
            <CustomTypography color={'#9e9e9e'} variant="caption">
              {t('bidding.results.created_by')}
            </CustomTypography>
            <CustomTypography>
              {[getCreatedByEmoji(createdBy), getCreatedByLabel(createdBy, t)].filter(Boolean).join(' ')}
            </CustomTypography>
          </Box>
        )}
      </Stack>
    </>
  )
}

type TableTextSummaryProps = {
  label: string
  text: string
}

const TableTextSummary = ({ label, text }: TableTextSummaryProps) => (
  <LabeledTextField label={label} text={text} variant="filled" />
)

export default BidVersionPtusToolbar
